/**
 * Which question of the exercise a line belongs to, as the learner checks it (#406 slice 3).
 *
 * The server proposes a label from the learner's own markers ("שאלה 5ב", "3)", "(ב)") and carries it down the page; this
 * shows it on every line, with where it came from, and lets the learner change it or say the line is part of no
 * question. Confirming the page confirms the labels too, so a label being changed blocks confirmation exactly as an open
 * text box does (`onEditingChange`, under its own key).
 *
 * - **The box is always EMPTY**, as every text box on this screen is: never pre-filled with the proposed label.
 * - **The label is untrusted handwriting or learner text**, rendered as plain React children in an isolated line
 *   (`UntrustedText`), never as HTML.
 * - **Nothing here says whether the work is right.** A label only says which question a line is part of.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HomeworkTranscriptionResponse } from '@davinci/contracts';
import { ApiRequestError } from '../../api/client.js';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import { UntrustedText, lineDir } from './untrusted-text.js';

type Region = HomeworkTranscriptionResponse['regions'][number];

/** The server's limit on a stored label, after trimming. */
const MAX_LABEL = 32;

export interface QuestionLabelProps {
  readonly region: Region;
  readonly pageId: string;
  readonly uploadId: string;
  readonly onEditingChange: (key: string, editing: boolean) => void;
}

/** "Question 5ב", or "No question": the label is untrusted text from the learner's page, so it goes through `UntrustedText`. */
export function QuestionWords({ label }: { readonly label: string | null }): React.JSX.Element {
  const t = useHomeworkLabels();
  return label === null ? (
    <>{t.noQuestion}</>
  ) : (
    <>
      {t.questionWord}{' '}
      <span className="handwriting" dir={lineDir(label)}>
        <UntrustedText text={label} />
      </span>
    </>
  );
}

export function QuestionLabel({ region, pageId, uploadId, onEditingChange }: QuestionLabelProps): React.JSX.Element {
  const t = useHomeworkLabels();
  const api = useApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const editingKey = `${region.regionId}#question`;

  useEffect(() => {
    onEditingChange(editingKey, editing);
  }, [onEditingChange, editingKey, editing]);
  useEffect(() => () => onEditingChange(editingKey, false), [onEditingChange, editingKey]);

  const save = useMutation({
    mutationFn: (label: string | null) =>
      api.setHomeworkQuestionLabel(pageId, region.regionId, crypto.randomUUID(), { label }),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.homeworkTranscription(pageId), updated);
      setText('');
      setEditing(false);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.homeworkTranscription(pageId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.homeworkUpload(uploadId),
      });
    },
  });

  const inputId = `region-${region.regionId}-question`;
  const hintId = `${inputId}-hint`;
  const failureSentence =
    save.error instanceof ApiRequestError ? t.labelFailureSentence(save.error.code) || undefined : undefined;

  // #646: a best guess (`questionHint`) fills the gap where no label is recorded. It reads as a `reader_marker` label does - the question, with no reason:
  // it can come from the line's own unsettled reading or another page, so "continued from the line above" would be untrue, and "no question" is not
  // what it says either.
  const hinted = hintShows(region);
  const shownLabel = hinted ? (region.questionHint as string) : region.questionLabel;
  const shownSource = hinted ? 'reader_marker' : region.questionLabelSource;
  return (
    <div className="region-label">
      <p className="region-state">
        {/* No <strong> or other element made around the text: the screen's own words, then the label as plain text. */}
        <span className="region-label-name">
          <QuestionWords label={shownLabel} />
        </span>
        {/* A reason only where it is not obvious: no question, or one carried from above. */}
        {shownLabel === null || shownSource === 'carried' ? (
          <> ({t.questionSource(shownSource)})</>
        ) : null}
      </p>

      {editing ? (
        <form
          className="region-box"
          onSubmit={(event) => {
            event.preventDefault();
            // The learner's own characters go as typed; the server owns trimming.
            save.mutate(text);
          }}
        >
          <div className="field">
            <label htmlFor={inputId}>{t.questionFieldLabel}</label>
            <p className="field-hint" id={hintId}>
              {t.questionFieldHint}
            </p>
            <input
              id={inputId}
              type="text"
              dir="auto"
              aria-describedby={hintId}
              maxLength={MAX_LABEL}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </div>
          <div className="region-actions">
            <button type="submit" disabled={save.isPending || text.trim() === ''}>
              {t.saveQuestion}
            </button>
            <button type="button" className="button-quiet" disabled={save.isPending} onClick={() => save.mutate(null)}>
              {t.notPartOfQuestion}
            </button>
            <button
              type="button"
              className="button-quiet"
              onClick={() => {
                save.reset();
                setText('');
                setEditing(false);
              }}
            >
              {t.cancel}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="button-link"
          onClick={() => {
            save.reset();
            setEditing(true);
          }}
        >
          {t.changeQuestion}
        </button>
      )}

      {save.isError ? (
        failureSentence !== undefined ? (
          <div className="notice notice-error" role="alert">
            <p>{failureSentence}</p>
          </div>
        ) : (
          <FailureNotice error={save.error} />
        )
      ) : null}
    </div>
  );
}

/** #646: a hint (display only) is shown only where no label is recorded and the record is not the learner's own clear. */
export function hintShows(region: Pick<Region, 'questionLabel' | 'questionLabelSource' | 'questionHint'>): boolean {
  return region.questionLabel === null && region.questionLabelSource !== 'learner' && region.questionHint !== undefined;
}

/** The question a line is shown under: the recorded label, else its hint, else none. */
export function shownQuestion(region: Pick<Region, 'questionLabel' | 'questionLabelSource' | 'questionHint'>): string | null {
  return hintShows(region) ? (region.questionHint as string) : region.questionLabel;
}
